import { ClassName, ModelType } from '@livv/models';
import { getModelTypeFromLawLex } from '@livv/utils/helpers';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Element } from 'domhandler/lib/node';
import { attributesToProps, DOMNode } from 'html-react-parser';
import DOMPurify from 'isomorphic-dompurify';
import InternalDocumentLink from '@components/Links/InternalDocumentLink';
import ArticleLink from '@screens/Books/Book/Nodes/Sheet/CodeArticle/ArticleLink';

export const cleanStyles = (html: string, stylesToKeep?: string[]): string => {
    const whitelistedStylesRegexStr = `(${stylesToKeep?.join('|') || '[\\w\\-]+'}):[ \\w_.\\-#%]+`;
    const whitelistedStylesRegex = new RegExp(whitelistedStylesRegexStr, 'g');
    const cleanContent = html.replace(/\s*style="[^"]+"/g, (match): string => {
        if (!stylesToKeep) return '';
        const matches = match.match(whitelistedStylesRegex);

        return matches ? ` style="${matches?.join('; ')}"` : '';
    });

    return cleanContent;
};

export const replaceTags = (html: string, tags: string[], replacement: string = '') =>
    tags.length
        ? html.replace(new RegExp(`<\\/?(${tags.join('|')})[^>]*>`, 'g'), replacement)
        : html;

const removeAnchors = (html: string): string => replaceTags(html, ['a']);

const removeImages = (html: string): string => replaceTags(html, ['img']);

const insertLawLexTags = (html: string): string =>
    html.replace(/(LawLex\d{12}JB[J|L|A])/g, '<span class="lawlexref">$1</span>');

export interface SanitizeOptions {
    cleanStylesExceptions?: string[];
    insertLawLexTags?: boolean;
    removeAnchors?: boolean;
    removeImages?: boolean;
}

export const sanitize = (content: string, options?: SanitizeOptions): string => {
    let sanitizedContent = DOMPurify.sanitize(content);
    if (options?.cleanStylesExceptions)
        sanitizedContent = cleanStyles(sanitizedContent, options.cleanStylesExceptions);
    if (options?.removeAnchors) sanitizedContent = removeAnchors(sanitizedContent);
    if (options?.removeImages) sanitizedContent = removeImages(sanitizedContent);
    if (options?.insertLawLexTags) sanitizedContent = insertLawLexTags(sanitizedContent);

    return sanitizedContent;
};

export const addInternalLinks = (domNode: DOMNode): JSX.Element | false => {
    // this casting is required as per discussion here : https://github.com/remarkablemark/html-react-parser/issues/221
    const { attribs, children, name } = domNode as unknown as Element;

    if (name !== 'span' || !attribs?.class) {
        return false;
    }

    const textChildren = children[0] as unknown as { data?: string };

    if (attribs.class === 'lawlexref') {
        const lawLexRef = textChildren?.data || '';

        return <InternalDocumentLink docId={lawLexRef}>{lawLexRef}</InternalDocumentLink>;
    }
    const lawlex = attribs['data-doc-id'];

    if (!lawlex || !textChildren?.data) {
        return false;
    }

    if (attribs.class === `entity-${ClassName.DECISION.toLowerCase()}`) {
        return (
            <InternalDocumentLink
                className={`entity-${ClassName.DECISION.toLowerCase()}`}
                docId={lawlex}
            >
                {textChildren.data}
            </InternalDocumentLink>
        );
    }
    if (attribs.class === `entity-${ClassName.LEGIS.toLowerCase()}`) {
        switch (getModelTypeFromLawLex(lawlex)) {
            case ModelType.LAW_TEXT:
                return (
                    <InternalDocumentLink
                        className={`entity-${ClassName.LEGIS.toLowerCase()}`}
                        docId={lawlex}
                    >
                        {textChildren.data}
                    </InternalDocumentLink>
                );
            default:
                return false;
        }
    }

    return false;
};

const extractArticleId = (href: string) => {
    const idArticleRegex = /(?:idArticle=)(.*?)(?=&)/g;
    const match = idArticleRegex.exec(href);

    return match ? match[1] : null;
};

const extractTextCid = (href: string) => {
    const cidTexteRegex = /(?:cidTexte=)(.*?)(?=&)/g;
    const match = cidTexteRegex.exec(href);

    return match ? match[1] : null;
};

export const addArticleLink = (domNode: DOMNode): JSX.Element | false => {
    // this casting is required as per discussion here : https://github.com/remarkablemark/html-react-parser/issues/221
    const typedDomNode = domNode as unknown as Element;

    if (typedDomNode.name === 'a' && typedDomNode?.attribs?.href) {
        const { href } = attributesToProps(typedDomNode.attribs);
        const textChildren = typedDomNode.children[0] as unknown as { data?: string };
        const label = textChildren?.data || '';
        const docId = extractArticleId(href) || extractTextCid(href);

        return docId ? (
            <ArticleLink
                articleId={docId}
                isInFirestore={extractArticleId(href) === null ? false : undefined}
                label={label}
            >
                {label}
            </ArticleLink>
        ) : (
            false
        );
    }

    return false;
};

export const stripHtmlTags = (string: string): string => string.replace(/<[^>]*>?/gm, '').trim();

const processParticularEmailDomains = (localName: string, domain: string) => {
    if (['gmail.com', 'googlemail.com', 'protonmail.com', 'proton.me', 'pm.me'].includes(domain)) {
        return localName.replace(/\./g, '');
    }

    return localName;
};

/**
 * For valid email, returns normalized email address, or returns original
 * for non-email strings.
 * In Microsoft, Apple and some other providers '.' matters, but is ignored
 * in google mail standard.
 * '+' with following suffix is ignored in both gmail and Microsoft standards.
 * @param email - user's email address
 * @returns - normalized email address
 */
export const normalizeEmail = (email: string): string => {
    const preprocessedEmail = email.toLowerCase().trim();
    const [localName, domain, remaining] = preprocessedEmail.split('@');
    // empty, empty localName, or empty domain, or no '@', or more than 1 '@' -- input is not processed
    if (
        !preprocessedEmail ||
        !localName ||
        !domain ||
        !email.includes('@') ||
        remaining !== undefined
    ) {
        return email;
    }

    const processedLocalName = processParticularEmailDomains(localName, domain);
    const localNamePrefix = processedLocalName.split('+')[0];

    return `${localNamePrefix}@${domain || ''}`;
};
